import useAnalytics from 'core/hooks/useAnalytics';
import { isTeams } from 'core/utils/teamsTab';
import { isNativeAppWebview } from 'core/utils/webapp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { EVENT_NAMES } from '../../../constants/eventNames';
import useStudentChatPracticeEnabled from '../../../hooks/useStudentChatPracticeEnabled';
import practiceProposalService from '../../../services/practiceProposalService';
import { buildPracticeLaunchLocation, getPracticeTabName } from '../../../utils/practiceLaunch';
import { shouldTrackProposalShown } from '../../../utils/practiceProposalAnalytics';
import { resolvePracticeProposalCardState } from './practiceProposalCardState';

import type { PracticeProposal } from '../../../types/practiceProposal';
import type { PracticeProposalCardAction, PracticeProposalCardStatus } from './practiceProposalCardState';

type Params = {
  conversationId: string | null;
  messageId: string;
  proposalId: string;
};

export type ChatPracticeProposalCardButton = {
  label: string;
  onSelect: () => void;
};

export type ChatPracticeProposalCardLesson = {
  id: string;
  title: string;
};

type UseChatPracticeProposalCard = {
  blockedTabMessage: string;
  isVisible: boolean;
  lessons: ChatPracticeProposalCardLesson[];
  primaryButton: ChatPracticeProposalCardButton | null;
  secondaryButton: ChatPracticeProposalCardButton | null;
  status: PracticeProposalCardStatus | null;
  statusText: string;
  title: string;
};

const ACTION_LABEL_KEYS: Record<PracticeProposalCardAction, string> = {
  again: 'chatbot:practice_proposal_action_again',
  continue: 'chatbot:practice_proposal_action_continue',
  retry: 'chatbot:practice_proposal_action_retry',
  start: 'chatbot:practice_proposal_action_start',
  summary: 'chatbot:practice_proposal_action_summary',
};

const STATUS_TEXT_KEYS: Partial<Record<PracticeProposalCardStatus, string>> = {
  completed: 'chatbot:practice_proposal_status_completed',
  creating: 'chatbot:practice_proposal_status_creating',
  failed: 'chatbot:practice_proposal_status_failed',
  unavailable: 'chatbot:practice_proposal_status_unavailable',
};

// Titles are server data shown as text, never as Markdown nor HTML. LMS titles may still carry
// markup (`<p>Lección 1</p>`), so it is reduced to its text with an inert DOMParser document, which
// runs no script and loads no image, unlike an `innerHTML` assignment.
function toPlainText(value: string): string {
  if (!value || globalThis.DOMParser === undefined) return value ?? '';
  return (new globalThis.DOMParser().parseFromString(value, 'text/html').body.textContent ?? '').trim();
}

function isSupportedSurface(): boolean {
  // The study tutor is not used in the native app nor in Teams (spec D7): there is no new tab to
  // open the practice in.
  return !isNativeAppWebview() && !isTeams();
}

/**
 * useChatPracticeProposalCard - The card of a practice the study tutor proposed (TANAI-2616). Reads
 * the proposal from the server (the tag in the message only carries its id) and opens the launcher
 * tab on click. Refreshes when the student comes back to this tab, so the card shows how the
 * practice went in the other one.
 */
function useChatPracticeProposalCard({ conversationId, messageId, proposalId }: Params): UseChatPracticeProposalCard {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const isFeatureEnabled = useStudentChatPracticeEnabled();
  const [isSurfaceSupported] = useState<boolean>(isSupportedSurface);
  const [proposal, setProposal] = useState<PracticeProposal | null>(null);
  const [reloadCount, setReloadCount] = useState<number>(0);
  const [isTabBlocked, setIsTabBlocked] = useState<boolean>(false);
  const hasTrackedShownRef = useRef<boolean>(false);

  const canLoad = isFeatureEnabled && isSurfaceSupported && Boolean(conversationId) && Boolean(proposalId);

  useEffect(() => {
    if (!canLoad) {
      setProposal(null);
      return undefined;
    }

    const controller = new AbortController();

    async function loadProposal(): Promise<void> {
      try {
        const loaded = await practiceProposalService.getProposal(conversationId as string, proposalId, { signal: controller.signal });
        if (!controller.signal.aborted) setProposal(loaded);
      } catch {
        // A failed refresh keeps what the card already shows; a failed first load shows nothing.
        // The service already logged it.
      }
    }

    loadProposal();
    return () => controller.abort();
  }, [canLoad, conversationId, proposalId, reloadCount]);

  useEffect(() => {
    if (!canLoad) return undefined;

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') setReloadCount((count) => count + 1);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [canLoad]);

  const cardState = useMemo(() => (proposal ? resolvePracticeProposalCardState(proposal) : null), [proposal]);
  // A tag the model copied into a later message points to a proposal of another turn: the card
  // only belongs to the message that proposed it.
  const isVisible = canLoad && cardState !== null && proposal?.messageId === messageId;

  useEffect(() => {
    if (!isVisible || !proposal || !conversationId || hasTrackedShownRef.current) return;
    hasTrackedShownRef.current = true;
    if (!shouldTrackProposalShown(proposal.proposalId)) return;

    Promise.resolve(
      trackEvent(EVENT_NAMES.PRACTICE_PROPOSAL_SHOWN, {
        conversation_id: conversationId,
        proposal_id: proposal.proposalId,
        target_type: proposal.targetType,
      })
    ).catch((trackingError: unknown) => {
      console.error('Analytics tracking failed:', trackingError);
    });
  }, [conversationId, isVisible, proposal, trackEvent]);

  // Synchronous on purpose, from the click to `window.open`: a single `await` before it would let the
  // browser treat the new tab as an unrequested popup and block it. The named target sends every
  // click on this card to the tab it already opened (a full navigation, so the tab runs the
  // bootstrap and the launcher again with the new key) instead of piling up practice tabs.
  const openLauncher = useCallback(
    (isNewAttempt: boolean): void => {
      if (!conversationId) return;
      const targetType = proposal?.targetType ?? null;
      const location = buildPracticeLaunchLocation({ conversationId, intentKey: uuidv4(), isNewAttempt, proposalId, targetType });
      const href = history?.createHref ? history.createHref(location) : `${location.pathname}${location.search}`;
      const practiceTab = window.open(href, getPracticeTabName(proposalId));
      setIsTabBlocked(practiceTab === null);
    },
    [conversationId, history, proposal?.targetType, proposalId]
  );

  const openCurrentAttempt = useCallback((): void => openLauncher(false), [openLauncher]);
  const openNewAttempt = useCallback((): void => openLauncher(true), [openLauncher]);

  const toButton = useCallback(
    (action: PracticeProposalCardAction | null): ChatPracticeProposalCardButton | null => {
      if (!action) return null;
      return { label: t(ACTION_LABEL_KEYS[action]), onSelect: action === 'again' ? openNewAttempt : openCurrentAttempt };
    },
    [openCurrentAttempt, openNewAttempt, t]
  );

  const statusText = useMemo((): string => {
    if (!cardState) return '';
    if (cardState.status === 'active' && cardState.maxActivityNumber) {
      return t('chatbot:practice_proposal_status_progress', { answered: cardState.answered ?? 0, total: cardState.maxActivityNumber });
    }
    const key = STATUS_TEXT_KEYS[cardState.status];
    return key ? t(key) : '';
  }, [cardState, t]);

  const lessons = useMemo(
    () => (proposal?.lessons ?? []).map((lesson) => ({ id: lesson.id, title: toPlainText(lesson.title) })).filter((lesson) => Boolean(lesson.title)),
    [proposal]
  );

  return {
    blockedTabMessage: isTabBlocked ? t('chatbot:practice_proposal_tab_blocked') : '',
    isVisible,
    lessons,
    primaryButton: toButton(cardState?.primaryAction ?? null),
    secondaryButton: toButton(cardState?.secondaryAction ?? null),
    status: cardState?.status ?? null,
    statusText,
    title: t('chatbot:practice_proposal_title'),
  };
}

export default useChatPracticeProposalCard;
